import React, { useEffect, useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Budget, Expense, BudgetType, ExpenseCategory, ExpenseApprovalStatus, Phase, Project } from '../../../types';
import {
  getProjectBudgets, createBudget, getProjectExpenses, submitExpense, decideExpense,
} from '../../../services/budgetService';
import { getProjectPhases } from '../../../services/milestoneService';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import FilterPills from '../../../components/FilterPills';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton, BMSuccessButton, BM_LABEL, BM_INPUT, BM_TITLE_INPUT,
} from '../../../components/BoardModal';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

const BTYPE_LABEL: Record<number, string> = { 100000000: 'Hours', 100000001: 'Currency' };
const BTYPE_BADGE: Record<number, { bg: string; fg: string }> = {
  100000000: { bg: '#D4EEF5', fg: '#0A5568' },
  100000001: { bg: '#D1FAE5', fg: '#065F46' },
};

const CAT_LABEL: Record<number, string> = {
  100000000: 'Travel', 100000001: 'Software', 100000002: 'Hardware',
  100000003: 'Contractor', 100000004: 'Facilities', 100000005: 'Other',
};
const CAT_BADGE: Record<number, { bg: string; fg: string }> = {
  100000000: { bg: '#EDE9FE', fg: '#4C1D95' },
  100000001: { bg: '#D4EEF5', fg: '#0A5568' },
  100000002: { bg: '#FEF3C7', fg: '#92400E' },
  100000003: { bg: '#D1FAE5', fg: '#065F46' },
  100000004: { bg: '#F3F4F6', fg: '#5A6272' },
  100000005: { bg: '#F3F4F6', fg: '#5A6272' },
};

const APPROVAL_LABEL: Record<number, string> = { 100000000: 'Pending', 100000001: 'Approved', 100000002: 'Rejected' };
const APPROVAL_BADGE: Record<number, { bg: string; fg: string }> = {
  100000000: { bg: '#FEF3C7', fg: '#92400E' },
  100000001: { bg: '#D1FAE5', fg: '#065F46' },
  100000002: { bg: '#FEE2E2', fg: '#991B1B' },
};

const CURRENCIES = ['USD', 'GBP', 'EUR', 'CAD', 'AUD'];

type ExpenseFilter = 'all' | 100000000 | 100000001 | 100000002;
const EXPENSE_FILTER_OPTIONS: { value: ExpenseFilter; label: string }[] = [
  { value: 'all',     label: 'All' },
  { value: 100000000, label: 'Pending' },
  { value: 100000001, label: 'Approved' },
  { value: 100000002, label: 'Rejected' },
];

function fmtCurrency(amount: number, currency: string | null | undefined): string {
  const c = currency || 'USD';
  try {
    return amount.toLocaleString(undefined, { style: 'currency', currency: c });
  } catch {
    return `${c} ${amount.toLocaleString()}`;
  }
}

export default function BudgetTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_budget">
      <BudgetTabBody {...props} />
    </ModuleGuard>
  );
}

function BudgetTabBody({ pca, projectId, project }: Props) {
  const [budgets, setBudgets]   = useState<Budget[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [phases, setPhases]     = useState<Phase[]>([]);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [filter, setFilter] = useState<ExpenseFilter>('all');
  const me = useCurrentUser(pca, projectId);

  const reload = async () => {
    const [b, e, p] = await Promise.all([
      getProjectBudgets(pca, projectId),
      getProjectExpenses(pca, projectId),
      getProjectPhases(pca, projectId).catch(() => [] as Phase[]),
    ]);
    setBudgets(b); setExpenses(e); setPhases(p);
  };
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  const projectCurrency = project.dagrun_pm_currency ?? 'USD';

  const totals = useMemo(() => {
    const allocated = budgets.reduce((s, b) => s + (b.dagrun_pm_allocatedamount ?? 0), 0);
    const spent     = expenses.filter(e => e.dagrun_pm_approvalstatus === ExpenseApprovalStatus.Approved)
                              .reduce((s, e) => s + (e.dagrun_pm_amount ?? 0), 0);
    return { allocated, spent, remaining: allocated - spent };
  }, [budgets, expenses]);

  const filteredExpenses = filter === 'all' ? expenses : expenses.filter(e => e.dagrun_pm_approvalstatus === filter);

  const phaseNameById: Record<string, string> = {};
  phases.forEach(p => { phaseNameById[p.dagrun_pm_phaseid] = p.dagrun_pm_name; });

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Budget</h3>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <SummaryCard label="Total Allocated" value={fmtCurrency(totals.allocated, projectCurrency)} />
        <SummaryCard label="Total Spent" value={fmtCurrency(totals.spent, projectCurrency)} accent="#0E7490" />
        <SummaryCard label="Remaining" value={fmtCurrency(totals.remaining, projectCurrency)} accent={totals.remaining < 0 ? '#DC2626' : '#059669'} />
      </div>

      {/* Allocations section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <SectionHeader>Budget Allocations</SectionHeader>
        <button type="button" onClick={() => setShowBudgetModal(true)}
          style={outlineBrandButton}
          onMouseEnter={e => (e.currentTarget.style.background = '#D4EEF5')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >+ Add Allocation</button>
      </div>
      {budgets.length === 0 ? (
        <div style={{ padding: 24, color: '#9198A8', fontSize: 13 }}>No budget allocations yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>Phase</Th><Th>Budget Name</Th><Th>Type</Th><Th>Amount</Th>
          </tr></thead>
          <tbody>
            {budgets.map(b => (
              <tr key={b.dagrun_pm_budgetid} style={{ borderBottom: '0.5px solid #EAECF0' }}>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{b._dagrun_pm_phaseid_value ? (phaseNameById[b._dagrun_pm_phaseid_value] ?? '—') : 'Project-wide'}</span></Td>
                <Td><span style={{ fontSize: 14, color: '#0F1117' }}>{b.dagrun_pm_name}</span></Td>
                <Td><Badge {...BTYPE_BADGE[b.dagrun_pm_budgettype]}>{BTYPE_LABEL[b.dagrun_pm_budgettype]}</Badge></Td>
                <Td><span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#0F1117' }}>{fmtCurrency(b.dagrun_pm_allocatedamount, b.dagrun_pm_currency ?? projectCurrency)}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Expenses section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <SectionHeader>Expenses</SectionHeader>
        <button type="button" onClick={() => setShowExpenseModal(true)} disabled={!me}
          style={{ ...outlineBrandButton, opacity: me ? 1 : 0.5 }}
          onMouseEnter={e => me && (e.currentTarget.style.background = '#D4EEF5')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >+ Submit Expense</button>
      </div>
      <div style={{ marginBottom: 8 }}>
        <FilterPills value={filter} options={EXPENSE_FILTER_OPTIONS} onChange={setFilter} />
      </div>
      {filteredExpenses.length === 0 ? (
        <div style={{ padding: 24, color: '#9198A8', fontSize: 13 }}>No expenses match this filter.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>Description</Th><Th>Amount</Th><Th>Category</Th><Th>Submitted By</Th><Th>On</Th><Th>Status</Th>
          </tr></thead>
          <tbody>
            {filteredExpenses.map(e => (
              <tr key={e.dagrun_pm_expenseid} onClick={() => setSelectedExpense(e)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={ev => (ev.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={ev => (ev.currentTarget.style.background = '')}>
                <Td><span style={{ fontSize: 14, color: '#0F1117' }}>{e.dagrun_pm_name}</span></Td>
                <Td><span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#0F1117' }}>{fmtCurrency(e.dagrun_pm_amount, e.dagrun_pm_currency ?? projectCurrency)}</span></Td>
                <Td><Badge {...CAT_BADGE[e.dagrun_pm_category]}>{CAT_LABEL[e.dagrun_pm_category]}</Badge></Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{e.dagrun_pm_submittedbyname ?? e.dagrun_pm_submittedby ?? '—'}</span></Td>
                <Td><span style={{ fontSize: 12, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>{e.dagrun_pm_submittedon ? new Date(e.dagrun_pm_submittedon).toLocaleDateString() : '—'}</span></Td>
                <Td><Badge {...APPROVAL_BADGE[e.dagrun_pm_approvalstatus]}>{APPROVAL_LABEL[e.dagrun_pm_approvalstatus]}</Badge></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showBudgetModal && (
        <AddBudgetModal pca={pca} projectId={projectId} phases={phases} projectCurrency={projectCurrency}
          onClose={() => setShowBudgetModal(false)}
          onCreated={() => { setShowBudgetModal(false); reload(); }}
        />
      )}
      {showExpenseModal && me && (
        <SubmitExpenseModal pca={pca} projectId={projectId} projectCurrency={projectCurrency} meOid={me.oid} meName={me.displayName}
          onClose={() => setShowExpenseModal(false)}
          onCreated={() => { setShowExpenseModal(false); reload(); }}
        />
      )}
      {selectedExpense && me && (
        <ExpenseDrawer pca={pca} expense={selectedExpense} isPM={me.isPM} meOid={me.oid} projectCurrency={projectCurrency}
          onClose={() => { setSelectedExpense(null); reload(); }}
        />
      )}
    </div>
  );
}

const outlineBrandButton: React.CSSProperties = {
  background: 'transparent', border: '1px solid #0E7490', color: '#0E7490',
  padding: '6px 12px', borderRadius: 6,
  fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600,
  cursor: 'pointer', transition: 'background 0.12s',
};

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <span style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</span>
);
const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);

function SummaryCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div style={{ flex: 1, minWidth: 160, background: '#F5F6F8', borderRadius: 8, padding: 16 }}>
      <div style={{ fontSize: 11, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: accent ?? '#0F1117', marginTop: 6, fontFamily: 'var(--font-display)' }}>{value}</div>
    </div>
  );
}

function AddBudgetModal({ pca, projectId, phases, projectCurrency, onClose, onCreated }: { pca: IPublicClientApplication; projectId: string; phases: Phase[]; projectCurrency: string; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState<number>(BudgetType.Currency);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(projectCurrency);
  const [phaseId, setPhaseId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const num = parseFloat(amount);
    if (!name.trim() || isNaN(num) || saving) return;
    setSaving(true); setError(null);
    try {
      await createBudget(pca, projectId, name.trim(), type, num, currency, phaseId || null);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Add Budget Allocation" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim() || !amount.trim()}>{saving ? 'Adding…' : 'Add Allocation'}</BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Phase</label>
        <select value={phaseId} onChange={e => setPhaseId(e.target.value)} style={BM_INPUT}>
          <option value="">No specific phase / Project-wide</option>
          {phases.map(p => <option key={p.dagrun_pm_phaseid} value={p.dagrun_pm_phaseid}>{p.dagrun_pm_name}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Budget Name *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} placeholder="e.g. Design phase" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Budget Type</label>
          <select value={type} onChange={e => setType(Number(e.target.value))} style={BM_INPUT}>
            <option value={BudgetType.Hours}>Hours</option>
            <option value={BudgetType.Currency}>Currency</option>
          </select>
        </div>
        <div>
          <label style={BM_LABEL}>Amount *</label>
          <input type="number" min={0} step="0.01" value={amount} onChange={e => setAmount(e.target.value)} style={BM_INPUT} />
        </div>
        <div>
          <label style={BM_LABEL}>Currency</label>
          <select value={currency} onChange={e => setCurrency(e.target.value)} style={BM_INPUT} disabled={type === BudgetType.Hours}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      {error && <div style={{ color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function SubmitExpenseModal({ pca, projectId, projectCurrency, meOid, meName, onClose, onCreated }: { pca: IPublicClientApplication; projectId: string; projectCurrency: string; meOid: string; meName: string; onClose: () => void; onCreated: () => void }) {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(projectCurrency);
  const [category, setCategory] = useState<number>(ExpenseCategory.Other);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const num = parseFloat(amount);
    if (!description.trim() || isNaN(num) || saving) return;
    setSaving(true); setError(null);
    try {
      await submitExpense(pca, projectId, {
        description: description.trim(), amount: num, currency, category,
        receipturl: receiptUrl.trim() || null,
        submittedby: meOid, submittedbyname: meName,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submit failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Submit Expense" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !description.trim() || !amount.trim()}>{saving ? 'Submitting…' : 'Submit'}</BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Description *</label>
        <input autoFocus value={description} onChange={e => setDescription(e.target.value)} style={BM_TITLE_INPUT} placeholder="e.g. Client site visit — Phoenix" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Amount *</label>
          <input type="number" min={0} step="0.01" value={amount} onChange={e => setAmount(e.target.value)} style={BM_INPUT} />
        </div>
        <div>
          <label style={BM_LABEL}>Currency</label>
          <select value={currency} onChange={e => setCurrency(e.target.value)} style={BM_INPUT}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Category</label>
        <select value={category} onChange={e => setCategory(Number(e.target.value))} style={BM_INPUT}>
          {Object.entries(CAT_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Receipt URL</label>
        <input value={receiptUrl} onChange={e => setReceiptUrl(e.target.value)} style={BM_INPUT} placeholder="Optional — link to receipt image or document" />
      </div>
      {error && <div style={{ color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function ExpenseDrawer({ pca, expense, isPM, meOid, projectCurrency, onClose }: { pca: IPublicClientApplication; expense: Expense; isPM: boolean; meOid: string; projectCurrency: string; onClose: () => void }) {
  const [draft, setDraft] = useState<Expense>(expense);
  const [acting, setActing] = useState<null | 'approve' | 'reject'>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => { setDraft(expense); }, [expense]);

  const decide = async (decision: 'approve' | 'reject') => {
    setActing(decision);
    try {
      await decideExpense(pca, draft.dagrun_pm_expenseid, decision, meOid);
      setDraft(d => ({
        ...d,
        dagrun_pm_approvalstatus: decision === 'approve' ? ExpenseApprovalStatus.Approved : ExpenseApprovalStatus.Rejected,
        dagrun_pm_approvedby: meOid,
      }));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } finally { setActing(null); }
  };

  const isFinal = draft.dagrun_pm_approvalstatus !== ExpenseApprovalStatus.Pending;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 480,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>{savedFlash ? 'Saved ✓' : ''}</span>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        <div style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', marginBottom: 8 }}>{draft.dagrun_pm_name}</div>
        <div style={{ marginBottom: 16, display: 'flex', gap: 6 }}>
          <Badge {...APPROVAL_BADGE[draft.dagrun_pm_approvalstatus]}>{APPROVAL_LABEL[draft.dagrun_pm_approvalstatus]}</Badge>
          <Badge {...CAT_BADGE[draft.dagrun_pm_category]}>{CAT_LABEL[draft.dagrun_pm_category]}</Badge>
        </div>

        <Eyebrow>Amount</Eyebrow>
        <Body><span style={{ fontFamily: 'var(--font-mono)', fontSize: 16 }}>{fmtCurrency(draft.dagrun_pm_amount, draft.dagrun_pm_currency ?? projectCurrency)}</span></Body>

        <Eyebrow>Submitted By</Eyebrow>
        <Body>{draft.dagrun_pm_submittedbyname ?? draft.dagrun_pm_submittedby ?? '—'}</Body>

        <Eyebrow>Submitted On</Eyebrow>
        <Body>{draft.dagrun_pm_submittedon ? new Date(draft.dagrun_pm_submittedon).toLocaleString() : '—'}</Body>

        <Eyebrow>Receipt</Eyebrow>
        <Body>
          {draft.dagrun_pm_receipturl
            ? <a href={draft.dagrun_pm_receipturl} target="_blank" rel="noreferrer" style={{ color: '#0E7490' }}>{draft.dagrun_pm_receipturl}</a>
            : '—'}
        </Body>

        {isPM && !isFinal && (
          <>
            <Eyebrow>PM Review</Eyebrow>
            <div style={{ display: 'flex', gap: 8 }}>
              <BMSuccessButton onClick={() => decide('approve')} disabled={acting !== null}>
                {acting === 'approve' ? 'Approving…' : 'Approve'}
              </BMSuccessButton>
              <BMDangerButton onClick={() => decide('reject')} disabled={acting !== null}>
                {acting === 'reject' ? 'Rejecting…' : 'Reject'}
              </BMDangerButton>
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: '#9198A8' }}>
              The expense schema does not have an approval-notes field; rationale must be communicated outside the app.
            </div>
          </>
        )}
      </div>
    </>
  );
}

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 16, marginBottom: 4, paddingTop: 12, borderTop: '0.5px solid #EAECF0' }}>{children}</div>
);
const Body = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 14, color: '#0F1117', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</div>
);
